import type {
  AIActivity,
  AICustomerProfile,
  AIFollowUp,
  AILead,
  AIConversation,
  IntelligenceOverview,
} from '../types/contracts';
import { apiFetch } from '../../../api/client';
import { parseJsonResponse } from '../../../api/parseResponse';
import type { IntelligenceAdapter, Result } from './adapter';

const BASE = '/platform/intelligence';

function normalizeConversation(c: AIConversation | Record<string, unknown>): AIConversation {
  const row = (c ?? {}) as Record<string, unknown>;
  const messages = Array.isArray(row.messages) ? row.messages : [];
  return {
    ...(row as AIConversation),
    id: String(row.id ?? ''),
    customer_id: String(row.customer_id ?? row.lead_id ?? ''),
    customer_name: String(row.customer_name ?? row.lead_name ?? ''),
    business_name: String(row.business_name ?? row.business ?? ''),
    messages,
  };
}

async function req<T>(p: Promise<unknown>): Promise<Result<T>> {
  const r = (await p) as { ok: boolean; data?: T; message?: string };
  return r.ok && r.data !== undefined
    ? { ok: true, data: r.data }
    : { ok: false, message: r.message || 'Request failed' };
}

async function get<T>(path: string) {
  const res = await apiFetch(path);
  return parseJsonResponse<T>(res);
}

async function post<T>(path: string, body: Record<string, unknown>) {
  const res = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseJsonResponse<T>(res);
}

/** Live adapter — same contract as the mock, backed by the Intelligence API. */
export class HttpIntelligenceAdapter implements IntelligenceAdapter {
  async getOverview(): Promise<Result<IntelligenceOverview>> {
    const r = await req<Record<string, unknown>>(get(`${BASE}/overview`));
    if (!r.ok) return r as Result<IntelligenceOverview>;
    const d = r.data as Record<string, unknown>;
    return {
      ok: true,
      data: {
        kpis: (d.kpis ?? []) as IntelligenceOverview['kpis'],
        attention: (d.attention ?? d.attention_queue ?? []) as IntelligenceOverview['attention'],
        live: (d.live ?? d.live_conversations ?? []) as IntelligenceOverview['live'],
        agents: (d.agents ?? d.agent_statuses ?? []) as IntelligenceOverview['agents'],
        notifications: (d.notifications ?? []) as IntelligenceOverview['notifications'],
      },
    };
  }
  async listConversations() {
    const r = await req<AIConversation[] | { items?: AIConversation[] }>(get(`${BASE}/conversations`));
    if (!r.ok) return r as Result<AIConversation[]>;
    const rows = Array.isArray(r.data) ? r.data : Array.isArray(r.data.items) ? r.data.items : [];
    return { ok: true, data: rows.map(normalizeConversation) };
  }
  async getConversation(id: string) {
    const r = await req<AIConversation>(get(`${BASE}/conversations/${encodeURIComponent(id)}`));
    if (!r.ok) return r;
    return { ok: true, data: normalizeConversation(r.data) };
  }
  getCustomerProfile(customerId: string) { return req<AICustomerProfile>(get(`${BASE}/customers/${encodeURIComponent(customerId)}`)); }
  listLeads() { return req<AILead[]>(get(`${BASE}/leads`)); }
  getLead(id: string) { return req<AILead>(get(`${BASE}/leads/${encodeURIComponent(id)}`)); }
  async getLeadTimeline(leadId: string): Promise<Result<AIActivity[]>> {
    const r = await req<AILead & { timeline?: AIActivity[] }>(get(`${BASE}/leads/${encodeURIComponent(leadId)}`));
    if (!r.ok) return { ok: false, message: (r as { message?: string }).message || 'Request failed' };
    return { ok: true, data: r.data.timeline ?? [] };
  }
  listFollowUps() { return req<AIFollowUp[]>(get(`${BASE}/follow-ups`)); }
  completeFollowUp(id: string) { return req<null>(post(`${BASE}/follow-ups/${encodeURIComponent(id)}/complete`, {})); }
  rescheduleFollowUp(id: string, dueAt: string) { return req<null>(post(`${BASE}/follow-ups/${encodeURIComponent(id)}/reschedule`, { due_at: dueAt })); }
  takeoverConversation(id: string) { return req<null>(post(`${BASE}/conversations/${encodeURIComponent(id)}/takeover`, {})); }
  resumeAi(id: string) { return req<null>(post(`${BASE}/conversations/${encodeURIComponent(id)}/resume-ai`, {})); }
}